import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { money } from "@/lib/api";
import TrustBadges from "@/components/TrustBadges";
import { Trash2, Minus, Plus, ShieldCheck, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const nav = useNavigate();
  const { cart, updateCartQty, removeFromCart } = useApp();
  const subtotal = cart.reduce((a, b) => a + b.price * b.qty, 0);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!subtotal) return setSummary(null);
    const p = new URLSearchParams({ subtotal: String(subtotal) });
    if (applied) p.set("code", applied);
    api.get(`/checkout/summary?${p.toString()}`).then(({ data }) => setSummary(data)).catch(() => {});
  }, [subtotal, applied]);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.get(
        `/coupons/validate?code=${encodeURIComponent(coupon.trim())}&subtotal=${subtotal}`
      );
      if (data.valid) {
        setApplied(coupon.trim().toUpperCase());
        toast.success(`Coupon applied — ₹${data.discount} off`);
      } else toast.error(data.detail || "Invalid coupon");
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (cart.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-32 text-center" data-testid="empty-cart">
        <h1 className="text-5xl font-serif tracking-tight">Your cart is empty</h1>
        <p className="mt-4 opacity-70">Discover the collection and find your next essential.</p>
        <Link to="/shop">
          <Button className="rounded-none mt-8 px-10 h-12 label-xs" data-testid="cart-shop-cta">
            Shop Now
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="cart-page">
      <h1 className="text-5xl font-serif tracking-tighter mb-12">
        Cart<span className="opacity-40 text-2xl align-top ml-3">({cart.length})</span>
      </h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {cart.map((item) => (
            <div
              key={`${item.product_id}-${item.size}`}
              className="flex gap-4 border-b pb-6"
              data-testid={`cart-item-${item.product_id}-${item.size}`}
            >
              <img src={item.image} alt={item.name} className="w-28 h-36 object-cover" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between">
                  <div>
                    <p className="font-serif text-xl">{item.name}</p>
                    <p className="label-xs opacity-60 mt-1">Size {item.size}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.product_id, item.size)}
                    aria-label="Remove"
                    data-testid={`cart-remove-${item.product_id}-${item.size}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex border">
                    <button
                      className="w-8 h-9"
                      onClick={() => updateCartQty(item.product_id, item.size, item.qty - 1)}
                    >
                      <Minus className="h-3 w-3 mx-auto" />
                    </button>
                    <span className="w-10 h-9 grid place-items-center border-x">{item.qty}</span>
                    <button
                      className="w-8 h-9"
                      onClick={() => updateCartQty(item.product_id, item.size, item.qty + 1)}
                    >
                      <Plus className="h-3 w-3 mx-auto" />
                    </button>
                  </div>
                  <div className="font-body">{money(item.price * item.qty)}</div>
                </div>
              </div>
            </div>
          ))}
          <div className="pt-4">
            <TrustBadges variant="compact" />
          </div>
        </div>

        <aside className="border p-8 h-fit lg:sticky lg:top-28" data-testid="cart-summary">
          <p className="label-xs mb-6">Order Summary</p>

          {/* Coupon */}
          <div className="mb-6">
            <p className="label-xs opacity-60 mb-2">Coupon</p>
            {applied ? (
              <div className="flex justify-between items-center border p-3" data-testid="cart-coupon-applied">
                <span className="text-sm">
                  <span className="font-serif">{applied}</span>
                  {summary?.discount ? ` · − ${money(summary.discount)}` : ""}
                </span>
                <button
                  onClick={() => {
                    setApplied(null);
                    setCoupon("");
                  }}
                  className="label-xs link-underline"
                  data-testid="cart-coupon-remove"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="rounded-none uppercase"
                  data-testid="cart-coupon-input"
                />
                <Button
                  onClick={applyCoupon}
                  variant="outline"
                  className="rounded-none border-foreground label-xs"
                  data-testid="cart-coupon-apply"
                >
                  Apply
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{money(summary?.subtotal ?? subtotal)}</span>
            </div>
            {summary?.discount ? (
              <div className="flex justify-between">
                <span>Discount</span>
                <span>− {money(summary.discount)}</span>
              </div>
            ) : null}
            <div className="flex justify-between">
              <span>Shipping</span>
              <span>{summary?.shipping ? money(summary.shipping) : <span className="font-serif">Free</span>}</span>
            </div>
            {summary && summary.free_shipping_threshold > 0 && summary.shipping ? (
              <p className="text-[11px] opacity-60">
                Add {money(summary.free_shipping_threshold - (summary.subtotal - summary.discount))} more for free shipping.
              </p>
            ) : null}
          </div>
          <div className="border-t mt-6 pt-4 flex justify-between text-lg">
            <span className="font-serif">Total</span>
            <span className="font-body" data-testid="cart-total">{money(summary?.total ?? subtotal)}</span>
          </div>
          <div className="mt-2 flex justify-between text-sm">
            <span className="opacity-70">Advance now ({summary?.advance_percent ?? 30}%)</span>
            <span className="font-body">{money(summary?.advance_amount ?? 0)}</span>
          </div>

          <Button
            onClick={() => nav("/checkout")}
            className="mt-6 w-full rounded-none h-12 label-xs"
            data-testid="cart-checkout-btn"
          >
            Proceed to Checkout
          </Button>

          <p className="mt-4 text-[11px] opacity-70 text-center flex items-center justify-center gap-2">
            <ShieldCheck className="h-3 w-3" />
            Secure advance payment · Remaining collected as per policy
          </p>

          <Link to="/shop" className="block text-center label-xs mt-6 link-underline">
            Continue Shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
